import { useQuery } from '@tanstack/react-query';
import {
  PENDING_DEVICE_COUNT_QUERY_KEY,
  deviceApprovalsApi,
} from '../api/deviceApprovals.api.js';

/**
 * How many browsers are waiting for an admin's decision.
 *
 * Modelled on `useUnreadNotificationCount` — same shape, same 60s cadence, same
 * shared query key — because it feeds the same `<UnreadBadge>` and the two must
 * not disagree about how a count behaves.
 *
 * Returns `null`, not 0, while the count is unknown (first load, or a failed
 * request). A badge is a claim about the world: rendering "0 pending" when the
 * request failed tells an admin there is nothing to review, which is the one
 * thing we do not know. `UnreadBadge` renders nothing at all for a count of 0,
 * so callers pass `count ?? 0` and an unknown count is silently absent rather
 * than confidently wrong.
 */
export function usePendingDeviceApprovalCount(
  options: { enabled?: boolean } = {}
): number | null {
  const { data, isError } = useQuery({
    queryKey: PENDING_DEVICE_COUNT_QUERY_KEY,
    queryFn: deviceApprovalsApi.pendingCount,
    refetchInterval: 60_000,
    enabled: options.enabled ?? true,
  });
  if (isError) return null;
  return data ?? null;
}
